import { AgentSessionType, PolicyAgentSession } from "@/modules/sessions/types";
import { UploadedFile } from "@/shared/types/file-upload";
import { NavigateFunction } from "react-router-dom";
import { ROUTES } from "@/shared/constants/routes";
import {
	AGENT_TYPES,
	AgentData,
	AgentSourceFile,
	GapAssessmentTypes,
	MultiVendorAssessmentTypes,
	PolicyManagementTypes,
	QuestionnaireTypes,
	RiskAssessmentTypes,
} from "../../types";
import { processFileForDoraGapAssessment } from "../../use-cases/dora.use-case";
import { processFileForFairAssessment } from "../../use-cases/fair.use-case";
import { processFilesForMapping } from "../../use-cases/quesitionnaire.use-case";
import { processFileForRiskAssessment } from "../../use-cases/risk.use-case";
import { processFileForCustomGapAssessment, processFileForSoc2GapAssessment } from "../../use-cases/soc2.use-case";
import { processDataForVendorAssessment, processFileForVendorReassessment } from "../../use-cases/vendor.use-case";
import { processDataForPolicyManagement } from "../../use-cases/policy.use-case";
//import { processDataForPolicyManagement } from "../../use-cases/policy.use-case";


export const processDataBasedOnAssessment = async (
	type: string | undefined,
	allFiles: UploadedFile[],
	navigate: NavigateFunction,
	inputs?: { [key: string]: string | boolean | string[] },
	selectedDropdownOption?: string | undefined,
	isToggleSelected?: boolean,
	reassessOption?: string,
	agentData?:
		| AgentData<
				AGENT_TYPES,
				| RiskAssessmentTypes
				| GapAssessmentTypes
				| QuestionnaireTypes
				| MultiVendorAssessmentTypes
				| PolicyManagementTypes
			>
		| undefined,
	prevSourceFiles?: AgentSourceFile[],
): Promise<string | undefined> => {
	const allFilesMap = allFiles.map((file) => {
		return {
			fileName: file.file.name ?? "",
			url: file.signedUrl ?? "",
			fileType: file.file.type,
		};
	});
	const allSourceFiles =
		prevSourceFiles && prevSourceFiles.length > 0
			? [...allFilesMap, ...prevSourceFiles]
			: [...allFilesMap];

	const name = (inputs?.title as string)?.trim() || ""

	switch (type) {
		case AGENT_TYPES.QUESTIONNAIRE: {
			const questionnaireFile = allFiles.filter((file) => file.type === "main");
			const kbFiles = allFiles.filter((file) => file.type === "kb");
			await processFilesForMapping({
				name,
				questionnaireFile: {
					file: questionnaireFile[0].file,
					url: questionnaireFile[0].signedUrl ?? "",
				},
				customer_name: (inputs?.customer_name as string) || "",
				sourceFiles: kbFiles.map((file) => {
					return {
						fileName: file.file.name,
						url: file.signedUrl ?? "",
					};
				}),
				navigate,
			});
			return;
		}
		case AGENT_TYPES.RISK_ASSESSMENT: {
			switch (selectedDropdownOption) {
				case RiskAssessmentTypes.NIST_CSF_2:
					await processFileForRiskAssessment({
						name,
						reassessOption,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							RiskAssessmentTypes
						>,
						sourceFiles: allSourceFiles.length > 0 ? allSourceFiles : [],
						evidence_mapping: (inputs?.evidence_mapping as string) === 'customize',
						subSessionType: selectedDropdownOption as (RiskAssessmentTypes.NIST_AI_RMF | RiskAssessmentTypes.NIST_CSF_2),
						navigate,
						isReassessmentMode: (inputs?.isReassessmentMode as boolean) || false,
					});
					break;
				case RiskAssessmentTypes.NIST_AI_RMF:
					await processFileForRiskAssessment({
						name,
						reassessOption,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							RiskAssessmentTypes
						>,
						sourceFiles: allSourceFiles.length > 0 ? allSourceFiles : [],	
						subSessionType: selectedDropdownOption as (RiskAssessmentTypes.NIST_AI_RMF | RiskAssessmentTypes.NIST_CSF_2),
						navigate,
					});
					break;
				case RiskAssessmentTypes.FAIR:
					await processFileForFairAssessment({
						name,
						risk_statement: (inputs?.risk_statement as string) || "",
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES.RISK_ASSESSMENT,
							RiskAssessmentTypes.FAIR
						>,
						sourceFiles: allSourceFiles,
						navigate,
					});
					break;
					default:
						throw new Error("Invalid risk assessment type");
			}
			return;
		}
		case AGENT_TYPES.GAP_ASSESSMENT:
			switch (selectedDropdownOption) {
				case RiskAssessmentTypes.NIST_CSF_2:
					await processFileForRiskAssessment({
						name,
						reassessOption,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							RiskAssessmentTypes
						>,
						sourceFiles: allSourceFiles.length > 0 ? allSourceFiles : [],
						evidence_mapping: (inputs?.evidence_mapping as string) === 'customize',
						subSessionType: selectedDropdownOption as (RiskAssessmentTypes.NIST_AI_RMF | RiskAssessmentTypes.NIST_CSF_2),
						navigate,
						isReassessmentMode: (inputs?.isReassessmentMode as boolean) || false,
					});
					break;
				case RiskAssessmentTypes.NIST_AI_RMF:
					await processFileForRiskAssessment({
						name,
						reassessOption,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							RiskAssessmentTypes
						>,
						sourceFiles: allSourceFiles.length > 0 ? allSourceFiles : [],	
						subSessionType: selectedDropdownOption as (RiskAssessmentTypes.NIST_AI_RMF | RiskAssessmentTypes.NIST_CSF_2),
						navigate,
					});
					break;
				case GapAssessmentTypes.DORA:
					await processFileForDoraGapAssessment({
						name,
						reassessOption,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							GapAssessmentTypes
						>,
						sourceFiles: allSourceFiles,
						navigate,
					});
					break;
				case GapAssessmentTypes.SOC2_TYPE1:
					await processFileForSoc2GapAssessment({
						name,
						customize: isToggleSelected || false,
						sessionType: AgentSessionType.SOC2_GAP_TYPE1,
						reassessOption,
						evidence_mapping: true,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							GapAssessmentTypes
						>,
						sourceFiles: allSourceFiles,
						navigate,
					});
					break;
				case GapAssessmentTypes.SOC2:
				case GapAssessmentTypes.SOC2_TYPE2:
					await processFileForSoc2GapAssessment({
						customize: isToggleSelected || false,
						sessionType: AgentSessionType.SOC2_GAP_TYPE2,
						name,
						reassessOption,
						evidence_mapping: true,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							GapAssessmentTypes
						>,
						sourceFiles: allSourceFiles,
						navigate,
					});
					break;
				default:
					await processFileForCustomGapAssessment({
						name,
						customize: isToggleSelected || false,
						sessionType: AgentSessionType.GAP_CUSTOM_FRAMEWORK,
						reassessOption,
						evidence_mapping: true,
						agentDataCurrent: agentData as AgentData<
							AGENT_TYPES,
							GapAssessmentTypes
						>,
						sourceFiles: allSourceFiles,
						navigate,
						frameworkId: selectedDropdownOption,
					});
			}
			return;
		case AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT:
      if (reassessOption) {
				await processFileForVendorReassessment({
					name,
					sourceFiles: allSourceFiles,
					navigate,
					reassessOption,
					agentDataCurrent: agentData as AgentData<
						AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
						MultiVendorAssessmentTypes.VENDOR_ASSESSMENT
					>,
					});
				return undefined;
			} else {
				const sessionId = await processDataForVendorAssessment({
					name,
					use_training_data: Boolean(inputs?.use_training_data),
					customize: (inputs?.customize as boolean) ?? false,
					multi_file_vendor_assessment: {
						vendor_name: (inputs?.vendorName as string) || "",
						vendor_filled_intake_form: Boolean(inputs?.requestVendor),
						questionnaire_url: (inputs?.questionnaire_url as string) || undefined,
						product_name: (inputs?.productName as string) || "",
						scope_of_integration: (inputs?.scope as string) || "",
						vendor_email: (inputs?.vendorEmail as string) || undefined,
						contact_email: (inputs?.contactEmail as string) || undefined,
						response_quality: (inputs?.response_quality as "accuracy" | "lite" | "speed") || "lite",
						doc_type: "json",
					},
					sourceFiles: allFiles.map((file) => ({
						fileName: file.file.name,
						url: file.signedUrl ?? "",
						fileType: file.file.type,
					})),
					navigate,
				});
				
				// Return the session ID
				return sessionId;
			}
			return;
		case AGENT_TYPES.POLICY_MANAGEMENT: {
			const kbFiles = allFiles.filter((file) => file.type === "kb");
			const deriveFiles = allFiles.filter((file) => file.type === "derive-document");
			const policyFile = allFiles.find((file) => file.type === "policy");
			
			// Prepare policy file for updating existing document (single file)
			const policyFileData = policyFile ? {
				fileName: policyFile.file.name,
				url: policyFile.signedUrl ?? "",
				fileType: policyFile.file.type,
			} : "";
			
			// Prepare derive files array for deriving from documents
			const allDeriveFiles = deriveFiles.map((file) => ({
				fileName: file.file.name,
				url: file.signedUrl ?? "",
				fileType: file.file.type,
			}));
			
			// Process policy management data
			await processDataForPolicyManagement({
				name: (inputs?.title as string) || "",
				documentation_name: (inputs?.policyName as string) || "",
				additional_instructions: (inputs?.context as string) || "",
				documentation_type: selectedDropdownOption || "Policy",
				documentation_action: (inputs?.action as string) || "create_new",
				compliance_frameworks: (inputs?.complianceFrameworks as string[]) || [],
				update_existing_document_url: policyFileData as AgentSourceFile,
				derive_from_documents_urls: allDeriveFiles,
				sourceFiles: kbFiles.map((file) => ({
					fileName: file.file.name,
					url: file.signedUrl ?? "",
					fileType: file.file.type,	
				})),
				navigate,
			});
			return;
		}

		default:
			throw new Error(`Invalid agent type : ${type}`);
	}
};

export const getCharacterLimit = (agentType: string | undefined, inputKey: string): number => {
	if (agentType === AGENT_TYPES.QUESTIONNAIRE && (inputKey === "title" || inputKey === "customer_name")) {
		return 50;
	}
	return 25;
};

// New validation function to check if input starts with special character
export const startsWithSpecialCharacter = (str: string): boolean => {
  if (!str || str.length === 0) return false;
  
  // Define special characters (excluding accented characters and international scripts)
  const specialCharRegex = /^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/;
  return specialCharRegex.test(str);
};

// Updated function to check for problematic characters (emojis and security risks)
export const containsProblematicCharacters = (str: string): boolean => {
  if (!str) return false;
  
  // Emoji and symbol ranges that might cause security issues
  const problematicRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE0F}]|[\u{200D}]/gu;
  
  return problematicRegex.test(str);
};

// Keep existing functions but mark them as deprecated
export const checkAlphaNumeric = (str: string): boolean => {
  // Deprecated - use startsWithSpecialCharacter instead
  return startsWithSpecialCharacter(str);
};

export const containsSpecialCharacters = (str: string): boolean => {
  // Deprecated - use startsWithSpecialCharacter instead
  return startsWithSpecialCharacter(str);
};

export const containsSpecialCharactersForTitle = (str: string): boolean => {
  // Updated to only check if starts with special character for title fields
  if (startsWithSpecialCharacter(str)) return true;
  
  // Also check for problematic characters in titles
  return containsProblematicCharacters(str);
};